import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Button, Card, Col, Form, Row, Table, Spinner, Badge, Modal,
} from "react-bootstrap";
import { FaPlus, FaCheck, FaBan, FaPrint, FaImage, FaPen, FaInfoCircle, FaLink } from "react-icons/fa";
import { Link } from "react-router-dom";
import { buildChips, countActiveChips, formatDateChip, resolveMapLabel } from "./utils/accountingFilterUtils";
import AccountingFilterSection from "./components/AccountingFilterSection";
import AccountingActiveFilters from "./components/AccountingActiveFilters";
import "./accountingFilters.css";
import "./accountingResponsive.css";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Select from "react-select";
import axiosInstance from "../../axiosInstance";
import { API_URL_All } from "../../api";
import AdLayout from "../../components/AdLayout";
import { Pagination } from "../../components/ui";
import { openVoucherPrintWindow, fmtDate } from "./utils/printVoucher";
import { formatCategoryLabel } from "./utils/categoryLabel";
import { formatVND as fmtVND, toMoneyNumber } from "./utils/moneyFormat";
import MoneyInput from "./components/MoneyInput";
import { fetchAccountingPermissions, ACCOUNTING_VOUCHER_PAYMENT } from "../../libs/customerCreatePermission";

const PAGE_SIZE = 20;
const DESC_TRUNCATE_LEN = 40;

const STATUS_MAP = {
  DRAFT: { label: "Nháp", bg: "secondary" },
  APPROVED: { label: "Đã duyệt", bg: "info" },
  CONFIRMED: { label: "Đã xác nhận", bg: "success" },
  CANCELLED: { label: "Đã huỷ", bg: "danger" },
};

const PAYMENT_TYPE_FILTER_OPTIONS = [
  { value: "AUTO_RECONCILIATION", label: "[AUTO] Chi thanh toán đối tác vận chuyển" },
  { value: "PAYABLE", label: "[MAN] Chi thanh toán công nợ đối tác" },
  { value: "SALARY", label: "[AUTO] Chi lương nhân viên" },
  { value: "ADVANCE", label: "[AUTO] Chi tạm ứng nhân viên" },
  { value: "ADVANCE_SUPPLEMENT", label: "[AUTO] Chi bổ sung tạm ứng nhân viên" },
  { value: "PREPAID", label: "[AUTO] Chi phí trả trước / CCDC" },
  { value: "EXPENSE", label: "[MAN] Chi phí vận hành" },
  { value: "OTHER", label: "[MAN] Chi nhập thủ công" },
];

const PAYMENT_TYPES = [
  { value: "PAYABLE", label: "[MAN] Chi thanh toán công nợ đối tác" },
  { value: "EXPENSE", label: "[MAN] Chi phí vận hành" },
  { value: "OTHER", label: "[MAN] Chi nhập thủ công" },
];

const paymentTypeLabel = (type, businessTypeLabel) =>
  businessTypeLabel || PAYMENT_TYPE_FILTER_OPTIONS.find((t) => t.value === type)?.label || type || "—";

const isEditablePaymentType = (paymentType) =>
  paymentType !== "AUTO_RECONCILIATION"
  && paymentType !== "SALARY"
  && paymentType !== "ADVANCE"
  && paymentType !== "ADVANCE_SUPPLEMENT"
  && paymentType !== "PREPAID";

const parseEvidencePaths = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
};

const resolveUploadUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${API_URL_All}${path}`;
};

const cardStyle = { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" };
const unwrap = (res) => res?.data?.data ?? res?.data ?? {};

const INITIAL_FORM = {
  targetName: "",
  targetId: "",
  amount: "",
  bankAccountId: "",
  lineIds: [],
  paymentType: "OTHER",
  categoryId: "",
  description: "",
};

const toLineIdList = (row) => {
  if (Array.isArray(row?.lineIds) && row.lineIds.length) {
    return row.lineIds.map((id) => String(id));
  }
  if (row?.lineId) return [String(row.lineId)];
  return [];
};

const EMPTY_PAYMENT_FILTERS = {
  keyword: "",
  partnerCode: "",
  status: "",
  bankAccountId: "",
  paymentType: "",
  dateFrom: "",
  dateTo: "",
};

const flattenCategories = (nodes, out = []) => {
  (nodes || []).forEach((node) => {
    out.push(node);
    if (node.children?.length) flattenCategories(node.children, out);
  });
  return out;
};

const PaymentVoucherList = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);

  const [bankAccounts, setBankAccounts] = useState([]);
  const [lines, setLines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expenseObjects, setExpenseObjects] = useState([]);

  const [filterKeyword, setFilterKeyword] = useState("");
  const [filterPartnerCode, setFilterPartnerCode] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterBankAccount, setFilterBankAccount] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterPaymentType, setFilterPaymentType] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_PAYMENT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [evidenceModal, setEvidenceModal] = useState(null);
  const [descriptionModal, setDescriptionModal] = useState(null);
  const [allocationModal, setAllocationModal] = useState(null);
  const [perm, setPerm] = useState({ canCreate: false, canEdit: false, canDelete: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchAccountingPermissions(axiosInstance, ACCOUNTING_VOUCHER_PAYMENT);
      if (!cancelled) {
        setPerm({
          canCreate: Boolean(p?.canCreate),
          canEdit: Boolean(p?.canEdit),
          canDelete: Boolean(p?.canDelete),
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const chipDefs = useMemo(() => [
    { key: "keyword", label: "Mã phiếu", isActive: (v) => Boolean(v) },
    { key: "partnerCode", label: "Mã đối tượng chi", isActive: (v) => Boolean(v) },
    {
      key: "bankAccountId",
      label: "TK chi",
      isActive: (v) => Boolean(v),
      formatValue: (v) => {
        const b = bankAccounts.find((a) => String(a.id) === String(v));
        return b ? `${b.bankName} - ${b.accountNumber}` : v;
      },
    },
    {
      key: "status",
      label: "Trạng thái",
      isActive: (v) => Boolean(v),
      formatValue: (v) => resolveMapLabel(v, STATUS_MAP),
    },
    {
      key: "paymentType",
      label: "Loại chi",
      isActive: (v) => Boolean(v),
      formatValue: (v) => PAYMENT_TYPE_FILTER_OPTIONS.find((o) => o.value === v)?.label || v,
    },
    {
      key: "dateRange",
      label: "Thời gian",
      isActive: (_, applied) => Boolean(applied.dateFrom || applied.dateTo),
      formatValue: (_, applied) => formatDateChip(applied.dateFrom, applied.dateTo),
    },
  ], [bankAccounts]);

  const activeChips = useMemo(
    () => buildChips(appliedFilters, chipDefs),
    [appliedFilters, chipDefs]
  );

  const fetchDropdowns = useCallback(async () => {
    try {
      const [bRes, lRes, catRes, objRes] = await Promise.all([
        axiosInstance.get("/api/accounting/bank-accounts/active").catch(() => null),
        axiosInstance.get("/api/accounting/payment-vouchers/lines").catch(() => null),
        axiosInstance.get("/api/accounting/expense-categories/tree").catch(() => null),
        axiosInstance.get("/api/accounting/expense-objects/active").catch(() => null),
      ]);
      const bankData = unwrap(bRes);
      const lineData = unwrap(lRes);
      if (bankData) {
        const accs = Array.isArray(bankData) ? bankData : bankData?.content || [];
        setBankAccounts(accs);
      }
      if (lineData) setLines(Array.isArray(lineData) ? lineData : []);
      const catData = unwrap(catRes);
      if (catData) setCategories(flattenCategories(Array.isArray(catData) ? catData : []));
      const objData = unwrap(objRes);
      if (objData) setExpenseObjects(Array.isArray(objData) ? objData : []);
    } catch (_) { /* silent */ }
  }, []);

  const fetchData = useCallback(async (p = 0, filters = appliedFilters) => {
    setLoading(true);
    try {
      const params = { page: p, size: PAGE_SIZE };
      if (filters.keyword) params.keyword = filters.keyword;
      if (filters.partnerCode) params.partnerCode = filters.partnerCode;
      if (filters.status) params.status = filters.status;
      if (filters.bankAccountId) params.bankAccountId = filters.bankAccountId;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.paymentType) params.type = filters.paymentType;

      const res = await axiosInstance.get("/api/accounting/payment-vouchers", { params });
      const body = unwrap(res);
      setRows(body.content || []);
      setTotalPages(body.totalPages || 0);
      setTotalElements(body.totalElements || 0);
      setPage(p);
    } catch (err) {
      toast.error("Không thể tải danh sách phiếu chi");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchDropdowns();
    fetchData(0, EMPTY_PAYMENT_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (next) => {
    setAppliedFilters(next);
    fetchData(0, next);
  };

  const syncDraftFromApplied = (filters) => {
    setFilterKeyword(filters.keyword || "");
    setFilterPartnerCode(filters.partnerCode || "");
    setFilterStatus(filters.status || "");
    setFilterBankAccount(filters.bankAccountId || "");
    setFilterPaymentType(filters.paymentType || "");
    setFilterFrom(filters.dateFrom || "");
    setFilterTo(filters.dateTo || "");
  };

  const handleSearch = () => {
    const next = {
      keyword: filterKeyword.trim(),
      partnerCode: filterPartnerCode.trim(),
      status: filterStatus,
      bankAccountId: filterBankAccount,
      paymentType: filterPaymentType,
      dateFrom: filterFrom,
      dateTo: filterTo,
    };
    applyFilters(next);
  };

  const handleReset = () => {
    syncDraftFromApplied(EMPTY_PAYMENT_FILTERS);
    applyFilters(EMPTY_PAYMENT_FILTERS);
  };

  const handleRemoveChip = (key) => {
    const base = { ...appliedFilters };
    if (key === "dateRange") {
      base.dateFrom = "";
      base.dateTo = "";
    } else {
      base[key] = "";
    }
    syncDraftFromApplied(base);
    applyFilters(base);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm(INITIAL_FORM);
  };

  const openCreate = () => {
    setEditId(null);
    setForm(INITIAL_FORM);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      targetName: row.payeeName || row.payeeCode || "",
      targetId: row.payeeId ? String(row.payeeId) : "",
      amount: row.amount != null ? String(row.amount) : "",
      bankAccountId: row.bankAccountId ? String(row.bankAccountId) : "",
      lineIds: toLineIdList(row),
      paymentType: row.paymentType || "OTHER",
      categoryId: row.categoryId ? String(row.categoryId) : "",
      description: row.description || "",
    });
    setShowModal(true);
  };

  const expenseObjectOptions = useMemo(() => {
    const opts = expenseObjects.map((o) => ({
      value: String(o.id),
      label: `${o.code || ""} — ${o.name || ""}`.trim(),
      categoryId: o.categoryId ? String(o.categoryId) : "",
      name: o.name || "",
    }));
    if (form.targetId && !opts.some((o) => o.value === String(form.targetId))) {
      opts.unshift({
        value: String(form.targetId),
        label: form.targetName || `#${form.targetId}`,
        categoryId: form.categoryId || "",
        name: form.targetName || "",
      });
    }
    return opts;
  }, [expenseObjects, form.targetId, form.targetName, form.categoryId]);

  const selectedExpenseObject = useMemo(
    () => expenseObjectOptions.find((o) => o.value === String(form.targetId)) || null,
    [expenseObjectOptions, form.targetId]
  );

  const lineOptions = useMemo(
    () => lines.map((l) => ({ value: String(l.id), label: l.name || l.lineName || `#${l.id}` })),
    [lines]
  );

  const selectedLines = useMemo(
    () => lineOptions.filter((o) => (form.lineIds || []).includes(o.value)),
    [lineOptions, form.lineIds]
  );

  const handleExpenseObjectChange = (opt) => {
    setForm((prev) => ({
      ...prev,
      targetId: opt?.value || "",
      targetName: opt?.name || "",
      categoryId: opt?.categoryId || prev.categoryId,
    }));
  };

  const onFormChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const buildPayload = () => {
    const lineIds = (form.lineIds || []).map((id) => Number(id)).filter((id) => !Number.isNaN(id));
    return {
      payeeName: form.targetName,
      payeeId: form.targetId ? Number(form.targetId) : null,
      payeeType: "EXPENSE_OBJECT",
      amount: toMoneyNumber(form.amount),
      bankAccountId: form.bankAccountId ? Number(form.bankAccountId) : null,
      lineIds,
      lineId: lineIds.length ? lineIds[0] : null,
      paymentType: form.paymentType,
      categoryId: form.categoryId ? Number(form.categoryId) : null,
      description: form.description,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.targetId || !form.amount || !form.bankAccountId || !form.categoryId) {
      toast.warn("Vui lòng chọn đối tượng chi và điền đầy đủ thông tin bắt buộc");
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const res = editId
        ? await axiosInstance.put(`/api/accounting/payment-vouchers/${editId}`, payload)
        : await axiosInstance.post("/api/accounting/payment-vouchers", payload);
      if (res?.data?.success === false) {
        toast.error(res.data.message || (editId ? "Cập nhật phiếu chi thất bại" : "Tạo phiếu chi thất bại"));
        return;
      }
      toast.success(editId ? "Cập nhật phiếu chi thành công" : "Tạo phiếu chi thành công");
      closeModal();
      fetchData(page);
    } catch (err) {
      toast.error(err.response?.data?.message || (editId ? "Cập nhật phiếu chi thất bại" : "Tạo phiếu chi thất bại"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (id) => {
    const result = await Swal.fire({
      title: "Xác nhận đúng, không chỉnh sửa gì thêm?",
      text: "Sau khi duyệt, phiếu sẽ khóa chỉnh sửa và phản ánh công nợ theo đối tượng chi.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Duyệt",
      cancelButtonText: "Huỷ",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axiosInstance.put(`/api/accounting/payment-vouchers/${id}/approve`);
      if (res?.data?.success === false) {
        toast.error(res.data.message || "Duyệt phiếu thất bại");
        return;
      }
      toast.success("Đã duyệt phiếu chi");
      fetchData(page);
    } catch (err) {
      toast.error(err.response?.data?.message || "Duyệt phiếu thất bại");
    }
  };

  const handleConfirm = async (id, paymentType) => {
    const isManual = isEditablePaymentType(paymentType);

    if (isManual) {
      const step = await Swal.fire({
        title: "Xác nhận đã chi và phản ánh vào dòng tiền?",
        text: "Công nợ đối tác sẽ được tất toán sau khi xác nhận.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Xác nhận",
        cancelButtonText: "Huỷ",
      });
      if (!step.isConfirmed) return;
    } else {
      const result = await Swal.fire({
        title: "Xác nhận phiếu chi?",
        text: "Phiếu chi sau khi xác nhận sẽ không thể chỉnh sửa.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Xác nhận",
        cancelButtonText: "Huỷ",
      });
      if (!result.isConfirmed) return;
    }

    try {
      const res = await axiosInstance.put(`/api/accounting/payment-vouchers/${id}/confirm`);
      if (res?.data?.success === false) {
        toast.error(res.data.message || "Xác nhận thất bại");
        return;
      }
      toast.success("Đã xác nhận phiếu chi");
      fetchData(page);
    } catch (err) {
      toast.error(err.response?.data?.message || "Xác nhận thất bại");
    }
  };

  const handleCancel = async (id) => {
    const result = await Swal.fire({
      title: "Huỷ phiếu chi?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Huỷ phiếu",
      cancelButtonText: "Đóng",
      confirmButtonColor: "#dc3545",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axiosInstance.put(`/api/accounting/payment-vouchers/${id}/cancel`);
      if (res?.data?.success === false) {
        toast.error(res.data.message || "Huỷ phiếu thất bại");
        return;
      }
      toast.success("Đã huỷ phiếu chi");
      fetchData(page);
    } catch (err) {
      toast.error(err.response?.data?.message || "Huỷ phiếu thất bại");
    }
  };

  const handlePrint = async (id) => {
    try {
      const res = await axiosInstance.get(`/api/accounting/payment-vouchers/${id}`);
      const v = unwrap(res);
      openVoucherPrintWindow("PHIẾU CHI", [
        ["Mã phiếu", v.code],
        ["Ngày chi", fmtDate(v.paymentDate)],
        ["Đối tượng chi", v.payeeCode || v.payeeName || (v.payeeId ? `#${v.payeeId}` : "—")],
        ["Line KD", v.lineName || "—"],
        ["Danh mục", v.categoryDisplayLabel || formatCategoryLabel(v.paymentType, v.categoryName, false)],
        ["Mô tả", v.description],
      ], v.amount, STATUS_MAP[v.status]?.label || v.status);
    } catch (_) {
      toast.error("Không thể in phiếu chi");
    }
  };

  const handleViewAllocations = async (id) => {
    try {
      const res = await axiosInstance.get(`/api/accounting/payment-vouchers/${id}/allocations`);
      const rows = unwrap(res);
      setAllocationModal({ id, rows: Array.isArray(rows) ? rows : [] });
    } catch (_) {
      toast.error("Không thể tải phân bổ công nợ");
    }
  };

  const renderDescription = (text) => {
    if (!text) return "—";
    if (text.length <= DESC_TRUNCATE_LEN) return text;
    return (
      <span className="d-inline-flex align-items-center gap-1">
        {text.slice(0, DESC_TRUNCATE_LEN)}…
        <FaInfoCircle
          className="text-primary flex-shrink-0"
          style={{ cursor: "pointer" }}
          title="Xem đầy đủ"
          onClick={() => setDescriptionModal(text)}
        />
      </span>
    );
  };


  const body = (
      <div className={embedded ? "" : "acc-page"}>
        <Card className="mb-3 acc-filter-card">
          <Card.Body className="p-0">
            <AccountingFilterSection
              title="Phiếu Chi"
              open={filterOpen}
              onToggle={setFilterOpen}
              onSubmit={handleSearch}
              onReset={handleReset}
              activeCount={countActiveChips(activeChips)}
              toolbarRight={perm.canCreate && (
                <Button variant="primary" size="sm" onClick={openCreate}>
                  <FaPlus className="me-1" /> Tạo phiếu chi
                </Button>
              )}
            >
              <Row className="g-2 align-items-end acc-filter-grid">
                <Col>
                  <Form.Label className="small mb-1">Mã phiếu</Form.Label>
                  <Form.Control size="sm" value={filterKeyword} onChange={(e) => setFilterKeyword(e.target.value)} placeholder="PC-... hoặc ID" />
                </Col>
                <Col>
                  <Form.Label className="small mb-1">Mã đối tượng chi</Form.Label>
                  <Form.Control
                    size="sm"
                    value={filterPartnerCode}
                    onChange={(e) => setFilterPartnerCode(e.target.value)}
                    placeholder="Nhập mã đối tượng chi..."
                  />
                </Col>
                <Col>
                  <Form.Label className="small mb-1">TK chi</Form.Label>
                  <Form.Select size="sm" value={filterBankAccount} onChange={(e) => setFilterBankAccount(e.target.value)}>
                    <option value="">Tất cả</option>
                    {bankAccounts.map((b) => (
                      <option key={b.id} value={b.id}>{b.bankName} - {b.accountNumber}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col>
                  <Form.Label className="small mb-1">Trạng thái</Form.Label>
                  <Form.Select size="sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="">Tất cả</option>
                    {Object.entries(STATUS_MAP).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col>
                  <Form.Label className="small mb-1">Loại chi</Form.Label>
                  <Form.Select size="sm" value={filterPaymentType} onChange={(e) => setFilterPaymentType(e.target.value)}>
                    <option value="">Tất cả</option>
                    {PAYMENT_TYPE_FILTER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col>
                  <Form.Label className="small mb-1">Từ ngày</Form.Label>
                  <Form.Control type="date" size="sm" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
                </Col>
                <Col>
                  <Form.Label className="small mb-1">Đến ngày</Form.Label>
                  <Form.Control type="date" size="sm" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
                </Col>
              </Row>
            </AccountingFilterSection>
            <AccountingActiveFilters
              chips={activeChips}
              onRemove={handleRemoveChip}
              onClearAll={handleReset}
            />
          </Card.Body>
        </Card>

        <Card style={cardStyle}>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : rows.length === 0 ? (
              <div className="text-center py-5 text-muted">Không có phiếu chi nào</div>
            ) : (
              <>
                <div className="acc-table-wrap">
                <Table striped hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Mã phiếu</th>
                      <th>Ngày chi</th>
                      <th className="acc-col-hide-sm">Đối tượng chi</th>
                      <th className="text-end">Số tiền</th>
                      <th className="text-center acc-col-hide-sm">TK chi</th>
                      <th className="acc-col-hide-sm">Line</th>
                      <th className="acc-col-hide-sm">Danh mục</th>
                      <th className="acc-col-hide-sm">Nội dung chi</th>
                      <th className="text-center">Trạng thái</th>
                      <th className="text-center">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => {
                      const evidencePaths = parseEvidencePaths(r.evidencePaths);
                      const isManual = isEditablePaymentType(r.paymentType);
                      const canEditRow = perm.canEdit && r.status === "DRAFT" && isManual;
                      const canCancelRow = perm.canEdit && (r.status === "DRAFT" || r.status === "APPROVED") && isManual;
                      return (
                      <tr key={r.id || idx}>
                        <td className="fw-semibold">{r.code || `#${r.id}`}</td>
                        <td>{r.paymentDate}</td>
                        <td className="acc-col-hide-sm">{r.payeeCode || r.payeeName || "—"}</td>
                        <td className="text-end fw-semibold">{fmtVND(r.amount)}</td>
                        <td className="text-center acc-col-hide-sm">{r.bankAccountName || "—"}</td>
                        <td className="acc-col-hide-sm">{r.lineName || "—"}</td>
                        <td className="small acc-col-hide-sm">{r.categoryDisplayLabel || formatCategoryLabel(r.paymentType, r.categoryName, false)}</td>
                        <td className="acc-col-hide-sm">
                          <div>{renderDescription(r.description)}</div>
                          {r.paymentType === "SALARY" && r.salarySheetId ? (
                            <Link
                              to={`/ke-toan/nhan-su?tab=salary&sheetId=${r.salarySheetId}`}
                              className="small d-inline-flex align-items-center gap-1 mt-1"
                              title="Mở bảng lương nguồn"
                            >
                              <FaLink />
                              Bảng lương{r.salarySheetPeriod ? `: ${r.salarySheetPeriod}` : ` #${r.salarySheetId}`}
                            </Link>
                          ) : null}
                          {r.paymentType === "PREPAID" && r.prepaidExpenseId ? (
                            <Link
                              to={`/ke-toan/nhan-su?tab=prepaid`}
                              className="small d-inline-flex align-items-center gap-1 mt-1"
                              title="Mở chi phí trả trước"
                            >
                              <FaLink />
                              CPT{r.prepaidExpenseCode ? `: ${r.prepaidExpenseCode}` : ` #${r.prepaidExpenseId}`}
                            </Link>
                          ) : null}
                        </td>
                        <td className="text-center">
                          <Badge bg={STATUS_MAP[r.status]?.bg || "secondary"}>
                            {STATUS_MAP[r.status]?.label || r.status}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <div className="acc-action-btns">
                            {canEditRow && (
                              <Button variant="outline-primary" size="sm" title="Sửa" onClick={() => openEdit(r)}>
                                <FaPen />
                              </Button>
                            )}
                            {perm.canEdit && r.status === "DRAFT" && isManual && (
                              <Button variant="outline-info" size="sm" title="Duyệt" onClick={() => handleApprove(r.id)}>
                                <FaCheck />
                              </Button>
                            )}
                            {perm.canEdit && r.status === "APPROVED" && isManual && (
                              <Button variant="outline-success" size="sm" title="Xác nhận đã chi" onClick={() => handleConfirm(r.id, r.paymentType)}>
                                <FaCheck />
                              </Button>
                            )}
                            {perm.canEdit && r.status === "DRAFT" && !isManual && (
                              <Button variant="outline-success" size="sm" title="Xác nhận" onClick={() => handleConfirm(r.id, r.paymentType)}>
                                <FaCheck />
                              </Button>
                            )}
                            {canCancelRow && (
                              <Button variant="outline-danger" size="sm" title="Huỷ" onClick={() => handleCancel(r.id)}>
                                <FaBan />
                              </Button>
                            )}
                            {evidencePaths.length > 0 && (
                              <Button variant="outline-info" size="sm" title="Xem chứng từ" onClick={() => setEvidenceModal(evidencePaths)}>
                                <FaImage />
                              </Button>
                            )}
                            <Button variant="outline-secondary" size="sm" title="In" onClick={() => handlePrint(r.id)}>
                              <FaPrint />
                            </Button>
                            {r.status === "CONFIRMED" && (
                              <Button variant="outline-warning" size="sm" title="Xem công nợ" onClick={() => handleViewAllocations(r.id)}>
                                <FaLink />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </Table>
                </div>
                <div className="acc-table-footer">
                  <small className="text-muted">Tổng: {totalElements} phiếu</small>
                  <Pagination
                    hidePaginationWhenSinglePage
                    showItemsPerPage={false}
                    pageData={{ page: { number: page, totalPages, totalElements, size: PAGE_SIZE } }}
                    onPageChange={(p) => fetchData(p - 1)}
                  />
                </div>
              </>
            )}
          </Card.Body>
        </Card>

        <Modal show={showModal} onHide={closeModal} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>{editId ? "Sửa phiếu chi" : "Tạo phiếu chi"}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Đối tượng chi <span className="text-danger">*</span></Form.Label>
                    <Select
                      options={expenseObjectOptions}
                      value={selectedExpenseObject}
                      onChange={handleExpenseObjectChange}
                      placeholder="Chọn đối tượng chi..."
                      isClearable
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Line KD</Form.Label>
                    <Select
                      options={lineOptions}
                      value={selectedLines}
                      onChange={(opts) => onFormChange("lineIds", (opts || []).map((o) => o.value))}
                      placeholder="Chọn một hoặc nhiều line..."
                      isMulti
                      isClearable
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Danh mục chi <span className="text-danger">*</span></Form.Label>
                    <Form.Select value={form.categoryId} onChange={(e) => onFormChange("categoryId", e.target.value)}>
                      <option value="">-- Chọn danh mục --</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.code ? `${c.code} - ` : ""}{c.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Tài khoản chi <span className="text-danger">*</span></Form.Label>
                    <Form.Select value={form.bankAccountId} onChange={(e) => onFormChange("bankAccountId", e.target.value)}>
                      <option value="">-- Chọn tài khoản --</option>
                      {bankAccounts.map((b) => (
                        <option key={b.id} value={b.id}>{b.bankName} - {b.accountNumber}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Số tiền <span className="text-danger">*</span></Form.Label>
                    <MoneyInput value={form.amount} onValueChange={(v) => onFormChange("amount", v)} placeholder="Nhập số tiền" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mô tả</Form.Label>
                    <Form.Control as="textarea" rows={1} value={form.description} onChange={(e) => onFormChange("description", e.target.value)} placeholder="Ghi chú..." />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeModal}>Đóng</Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? <Spinner animation="border" size="sm" /> : editId ? "Cập nhật" : "Tạo phiếu chi"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={!!allocationModal} onHide={() => setAllocationModal(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Phân bổ công nợ — Phiếu #{allocationModal?.id}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {(allocationModal?.rows || []).length === 0 ? (
              <div className="text-muted">Chưa có phân bổ công nợ</div>
            ) : (
              <Table striped size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Khoản CN</th>
                    <th className="text-end">Số tiền</th>
                    <th>Ngày</th>
                  </tr>
                </thead>
                <tbody>
                  {(allocationModal?.rows || []).map((a) => (
                    <tr key={a.id}>
                      <td>#{a.debtId}</td>
                      <td className="text-end">{fmtVND(a.amount)}</td>
                      <td>{a.createdAt || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Modal.Body>
        </Modal>

        <Modal show={!!descriptionModal} onHide={() => setDescriptionModal(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Nội dung chi</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ whiteSpace: "pre-wrap" }}>{descriptionModal}</Modal.Body>
        </Modal>

        <Modal show={!!evidenceModal} onHide={() => setEvidenceModal(null)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>Chứng từ thanh toán</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex flex-wrap gap-3">
              {(evidenceModal || []).map((path, idx) => (
                <a key={idx} href={resolveUploadUrl(path)} target="_blank" rel="noopener noreferrer">
                  <img src={resolveUploadUrl(path)} alt={`evidence-${idx}`} className="rounded border" style={{ maxWidth: 200, maxHeight: 200, objectFit: "cover" }} />
                </a>
              ))}
            </div>
          </Modal.Body>
        </Modal>
      </div>
  );

  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};

export default PaymentVoucherList;
